import { gql } from '@apollo/client';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Dialog } from '@headlessui/react';
import { Auth } from 'aws-amplify';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import images from 'src/common/images';
import { ErrorMstControl, MstControl, MstHinmei, MstProduct } from 'src/common/interfaces';
import { Condition1 } from 'src/common/string-enum';
import { ErrorMessage, InfoMessage, InfoWord } from 'src/enum/messsage-enum';
import { registerMstControlRule } from 'src/graphql/mutations';
import { getMstControlRule, getMstHinmei, getMstProduct } from 'src/graphql/queries';
import { useAppLazyQuery, useAppMutation } from 'src/libs/hooks';
import utility from 'src/libs/utility';
import ModalGuideHinmeiForControlRule from '../guide-hinmei-for-control-rule/guide-hinmei-for-control-rule';
import ModalGuideProduct, { MprProduct } from '../guide-product/guide-product';
import MainLayout from '../main-layout';

const defaultErrorMstControl = {
  isErrorControlCd: false,
  isErrorCondition1: false,
  isErrorCondition2: false,
  isErrorCondition3: false,
  isErrorControlitem: false,
  isErrorControlkb: false,
};

const emptyControl = {
  mcr_process_order: null,
  mcr_condition1: null,
  mcr_condition2: null,
  mcr_condition3: null,
  mcr_control_item: null,
  mcr_control_kb: null,
  isCannotEditControlCd: false,
  error: defaultErrorMstControl,
  isBeginData: false,
  mpr_product_cd: '',
  mpr_product_nm: '',
  mh_hin_cd: '',
  mh_hin_nm: '',
  mh_hin2_nm: '',
};

type ModalProps = {
  open: boolean;
  index?: number;
};

const ControlRule = () => {
  const header = ['処理順', '条件１', '条件２', '条件３', '制御対象', '制御内容', '行移動', '削除'];
  const [mstControl, setMstControl] = useState<Array<MstControl>>([]);
  const { confirmAlert, showAlertMessage } = utility;
  const [removedMstControl, setRemovedMstControl] = useState<Array<MstControl>>([]);
  const [beginLengthListControl, setBeginLengthListControl] = useState<number>(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuthenticator((context) => [context.user]);
  const [registerFunc] = useAppMutation({ query: gql(registerMstControlRule) });
  const [modalhinmei, setModalhinmei] = useState<ModalProps>({ open: false });
  const [modalproduct, setModalproduct] = useState<ModalProps>({ open: false });
  const [modalhinmeicontrol, setModalhinmeicontrol] = useState<ModalProps>({ open: false });
  const [searchMstControl, { data: mstControlData, loading: isMstControlLoading }] = useAppLazyQuery({
    query: gql(getMstControlRule),
    onCompleted: () => {
      setRemovedMstControl([]);
    },
  });
  const [searchMstProduct, { data: mstProductData }] = useAppLazyQuery({ query: gql(getMstProduct) });
  const [searchMstHinmei, { data: mstHinmeiData }] = useAppLazyQuery({ query: gql(getMstHinmei) });

  // 製品区分をコードと名称でマッピングしたObject
  const productMapping = useMemo(() => {
    if (!mstProductData) return;
    return mstProductData.getMstProduct.reduce(
      (prevVal: { [key: string]: string }, v: MstProduct) => ({ ...prevVal, [v.mpr_product_cd]: v.mpr_product_nm }),
      {}
    );
  }, [mstProductData]);

  // 品名をコードと名称でマッピングしたObject
  const hinmeiMapping = useMemo(() => {
    if (!mstHinmeiData) return;
    return mstHinmeiData.getMstHinmei.reduce(
      (prevVal: { [key: string]: string }, v: MstHinmei) => ({ ...prevVal, [v.mh_hin_cd]: v.mh_hin_nm }),
      {}
    );
  }, [mstHinmeiData]);

  const handleChangeControl = (index: number, targetObj: { [key: string]: string }) => {
    const newListMstControl = mstControl.slice();
    newListMstControl[index] = { ...newListMstControl[index], ...targetObj };
    const errorObject: ErrorMstControl = { ...newListMstControl[index].error };

    switch (targetObj.key) {
      case 'mcr_process_order':
        errorObject.isErrorControlCd = false;
        break;
      case 'mcr_condition1':
        errorObject.isErrorCondition1 = false;
        break;
      case 'mcr_condition2':
        errorObject.isErrorCondition2 = false;
        break;
      case 'mcr_condition3':
        errorObject.isErrorCondition3 = false;
        break;
      case 'mcr_control_item':
        errorObject.isErrorControlitem = false;
        break;
      case 'mcr_control_kb':
        errorObject.isErrorControlkb = false;
        break;
    }
    newListMstControl[index].error = { ...newListMstControl[index].error, ...errorObject };
    setMstControl(newListMstControl);
  };

  const renderTableData = useMemo(() => {
    if (!mstControl || mstControl.length <= 0) return;
    const styleInput = (isError: boolean | undefined) => {
      return `${isError ? 'border-[#FF0000] border-[2px] rounded-md' : 'border-{transparent}'} pl-[5px] bg-transparent`;
    };
    const styleModalInput = (isError: boolean | undefined) => {
      return `${
        isError ? 'border-[#FF0000] border-[2px] rounded-md' : 'border-{transparent}'
      } pl-[5px] bg-transparent h-[40px] w-full`;
    };

    return mstControl.map((control: MstControl, index: number) => {
      const isLastItem = index === mstControl.length - 1;

      return (
        <tr className="hover:opacity-80 odd:bg-[#e2efd9]" key={index}>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <div className={`${styleInput(control.error.isErrorControlCd)} text-right`}>
              {control.mcr_process_order}
            </div>
          </td>
          {/* 条件１ */}
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <select
              className={styleInput(control.error.isErrorCondition1)}
              name="mcr_condition1"
              onChange={(event) => {
                handleChangeControl(index, {
                  mcr_process_order: index.toString(),
                  mcr_condition1: event.target.value,
                  mcr_condition2: '0',
                  mcr_condition3: '0',
                });
              }}
              value={control.mcr_condition1 ?? ''}
            >
              <option value="0"></option>
              <option value="1">常に</option>
              <option value="2">製品区分に</option>
              <option value="3">構成に</option>
              <option value="4">品名に</option>
            </select>
          </td>
          {/* 条件２ */}
          {(() => {
            if (!control.mcr_condition1 || control.mcr_condition1.toString() === '0') {
              return (
                <td className="p-[8px] border-[1px] border-solid border-[#fff]">
                  <div className={styleModalInput(control.error.isErrorCondition2)}></div>
                </td>
              );
            }
            switch (control.mcr_condition1.toString()) {
              case Condition1.ALL:
                return (
                  <td className="p-[8px] border-[1px] border-solid border-[#fff]">
                    <select className="bg-transparent pl-[5px]" name="mcr_condition2" value={''} disabled={true}>
                      <option value="0"></option>
                    </select>
                  </td>
                );
              case Condition1.PRODUCT.toString():
                return (
                  <td
                    className="p-[8px] border-[1px] border-solid border-[#fff]"
                    onClick={() => setModalproduct({ open: true, index })}
                  >
                    <button className={styleModalInput(control.error.isErrorCondition2)}>
                      {control.mpr_product_nm}
                    </button>
                  </td>
                );
              case Condition1.CONFIGURATION.toString():
                return (
                  <td className="p-[8px] border-[1px] border-solid border-[#fff]">
                    <select
                      className={styleInput(control.error.isErrorCondition2)}
                      name="mcr_condition2"
                      onChange={(event) => {
                        handleChangeControl(index, {
                          mcr_condition2: event.target.value,
                        });
                      }}
                      value={control.mcr_condition2 ?? ''}
                      disabled={false}
                    >
                      <option value="0"></option>
                      <option value="1">セットモデル</option>
                      <option value="2">アラカルト</option>
                    </select>
                  </td>
                );
              case Condition1.HINMEI.toString():
                return (
                  <td
                    className="p-[8px] border-[1px] border-solid border-[#fff]"
                    onClick={() => setModalhinmei({ open: true, index })}
                  >
                    <button className={styleModalInput(control.error.isErrorCondition2)}>{control.mh_hin2_nm}</button>
                  </td>
                );
            }
          })()}
          {/* </td> */}
          {/* 条件３ */}
          {(() => {
            if (!control.mcr_condition1 || control.mcr_condition1.toString() === '0') {
              return (
                <td className="p-[8px] border-[1px] border-solid border-[#fff]">
                  <div className={styleModalInput(control.error.isErrorCondition2)}></div>
                </td>
              );
            }
            return (
              <td className="p-[8px] border-[1px] border-solid border-[#fff]">
                <select
                  className={
                    control.mcr_condition1.toString() === Condition1.ALL
                      ? 'bg-transparent pl-[5px]'
                      : styleInput(control.error.isErrorCondition3)
                  }
                  name="mcr_condition3"
                  onChange={(event) => {
                    handleChangeControl(index, {
                      mcr_process_order: index.toString(),
                      mcr_condition3: event.target.value,
                    });
                  }}
                  value={control.mcr_condition3 ?? ''}
                  disabled={control.mcr_condition1.toString() === '1' ? true : false}
                >
                  <option value="0"></option>
                  <option value="1">{control.mcr_condition1.toString() !== '1' ? '選択されている場合' : ''}</option>
                  <option value="2">{control.mcr_condition1.toString() !== '1' ? '選択されていない場合' : ''}</option>
                </select>
              </td>
            );
          })()}

          {/* 制御対象 */}
          <td
            className="p-[8px] border-[1px] border-solid border-[#fff]"
            onClick={() => setModalhinmeicontrol({ open: true, index })}
            onChange={() => {
              handleChangeControl(index, {
                mcr_process_order: index.toString(),
              });
            }}
          >
            <button className={styleModalInput(control.error.isErrorControlitem)}>{control.mh_hin_nm}</button>
          </td>
          {/* 制御内容 */}
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <select
              className={styleInput(control.error.isErrorControlkb)}
              name="mcr_control_kb"
              onChange={(event) => {
                handleChangeControl(index, {
                  mcr_process_order: index.toString(),
                  mcr_control_kb: event.target.value,
                });
              }}
              value={control.mcr_control_kb ?? ''}
            >
              <option value="0"></option>
              <option value="1">選択できる</option>
              <option value="2">選択できない</option>
            </select>
          </td>
          {/* 行移動 */}
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <button onClick={() => onPressMove(index, 1)}>
              {!isLastItem ? <img src={images.icDownMove} alt="" className="w-[20px]" /> : null}
            </button>
            <button onClick={() => onPressMove(index, -1)}>
              {!isLastItem ? <img src={images.icUPMove} alt="" className="w-[20px]" /> : null}
            </button>
          </td>
          {/* 削除 */}
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <button onClick={() => onConfirmRemove(index)}>
              {!isLastItem ? <img src={images.icDelete} alt="" className="w-[20px]" /> : null}
            </button>
          </td>
        </tr>
      );
    });
  }, [mstControl]);

  const onCallbackHinmeiFunction = (data: MstHinmei) => {
    handleChangeControl(modalhinmei.index as number, { mcr_condition2: data.mh_hin_cd, mh_hin2_nm: data.mh_hin_nm });
  };

  const onCallbackProductFunction = (data: MprProduct) => {
    handleChangeControl(modalproduct.index as number, {
      mcr_condition2: data.mpr_product_cd as unknown as string,
      mpr_product_nm: data.mpr_product_nm as unknown as string,
    });
  };

  const onCallbackHinmeicontrolFunction = (data: MstHinmei) => {
    handleChangeControl(modalhinmeicontrol.index as number, {
      mcr_process_order: modalhinmeicontrol.index as unknown as string,
      mcr_control_item: data.mh_hin_cd,
      mh_hin_nm: data.mh_hin_nm,
    });
  };

  const renderTableHeader = useMemo(() => {
    return header.map((key, index) => (
      <th
        className="p-[8px] border-[1px] border-solid border-[#fff] pt-[12px] pb-[12px] text-center bg-[#a8d08d] text-[#fff]"
        key={index}
      >
        {key.toUpperCase()}
      </th>
    ));
  }, []);

  const isEmptyData = (data: MstControl) => {
    return (
      data.mcr_process_order === null &&
      data.mcr_condition1 === null &&
      data.mcr_condition2 === null &&
      data.mcr_condition3 === null &&
      data.mcr_control_item === null &&
      data.mcr_control_kb === null
    );
  };

  //行削除
  const onPressRemove = (index: number) => {
    const newListControl = [...mstControl];
    if (index < beginLengthListControl) {
      const newListRemove = [...removedMstControl];
      setBeginLengthListControl(beginLengthListControl - 1);
      setRemovedMstControl(newListRemove);
    }
    newListControl.splice(index, 1);

    //画面から該当行を削除した後に処理順の表示を調整
    let maxIndex = 0;
    for (let i = index; i < newListControl.length; i++) {
      newListControl[i].mcr_process_order = i;
      maxIndex = i;
    }
    newListControl[maxIndex].mcr_process_order = null;
    newListControl.splice(maxIndex, 1);
    setMstControl(newListControl);
  };

  //行移動
  const onPressMove = (index: number, distance: number) => {
    const newListControl = [...mstControl];
    // 最上行の場合、move upをさせない
    if (index + distance < 0) return;
    // 最下行の場合、move downをさせない
    if (index + distance + 1 >= mstControl.length) return;
    // 処理順を調整
    const processOrder = Number(newListControl[index].mcr_process_order);
    const dinstanceOrder = Number(newListControl[index + distance].mcr_process_order);

    // Typescriptでのエラー回避
    if (!processOrder.toString()) {
      return;
    }
    if (!dinstanceOrder.toString()) {
      return;
    }
    // 処理順を更新
    newListControl[index].mcr_process_order = processOrder + distance;
    newListControl[index + distance].mcr_process_order = dinstanceOrder - distance;

    // 配列置き換え
    newListControl.splice(index + distance, 0, newListControl.splice(index, 1)[0]);
    setMstControl(newListControl);
  };

  //行削除
  const onConfirmRemove = (index: number) => {
    confirmAlert(InfoMessage.CONFIRM_REMOVE, '', InfoWord.WARNING, InfoWord.YES, InfoWord.NO, () =>
      onPressRemove(index)
    );
  };

  //登録ボタン
  const onPressConfirmRegister = () => {
    Auth.currentAuthenticatedUser()
      .then(() =>
        confirmAlert(InfoMessage.CONFIRM_REGISTR, '', InfoWord.WARNING, InfoWord.YES, InfoWord.NO, onRegisterData)
      )
      .catch(() => {
        signOut();
        navigate('/login', { replace: true, state: { from: location } });
      });
  };

  const checkError = (itemControl: MstControl) => {
    const isErrorControlCd = itemControl.mcr_process_order === null;
    const isErrorCondition1 = itemControl.mcr_condition1 === null || itemControl.mcr_condition1.toString() === '0';
    const isErrorCondition2 =
      (itemControl.mcr_condition1 !== 1 && itemControl.mcr_condition2 === null) ||
      (itemControl.mcr_condition1 !== 1 && itemControl.mcr_condition2 === 0) ||
      (itemControl.mcr_condition1 !== 1 && itemControl.mcr_condition2?.toString() === '0');
    const isErrorCondition3 =
      (itemControl.mcr_condition1 !== 1 && itemControl.mcr_condition3 === null) ||
      (itemControl.mcr_condition1 !== 1 && itemControl.mcr_condition3 === 0) ||
      (itemControl.mcr_condition1 !== 1 && itemControl.mcr_condition3?.toString() === '0');
    const isErrorControlitem = itemControl.mcr_control_item === null;
    const isErrorControlkb = itemControl.mcr_control_kb === null;
    return {
      isErrorControlCd,
      isErrorCondition1,
      isErrorCondition2,
      isErrorCondition3,
      isErrorControlitem,
      isErrorControlkb,
    };
  };

  const isExistItem = () => {
    const newListControl = [...mstControl];
    const valueArr = newListControl.map(function (item) {
      return item.mcr_process_order;
    });
    const isDuplicate = valueArr.some(function (item, idx) {
      if (valueArr.indexOf(item) !== idx) {
        newListControl[idx].error.isErrorControlCd = true;
        return true;
      }
      return false;
    });
    setMstControl(newListControl);
    return isDuplicate;
  };

  const validateData = () => {
    if (isExistItem()) {
      showAlertMessage('', InfoMessage.REGISTR_DUPLICATION, InfoWord.ERROR, InfoWord.OK);
      return false;
    }
    const newListControl = [...mstControl];
    newListControl.pop();
    const newListAddError = newListControl.map((itemControl: MstControl) => {
      return { ...itemControl, error: checkError(itemControl) };
    });
    setMstControl(newListAddError);
    const indexItemError = newListControl.findIndex(
      (itemControl: MstControl) =>
        itemControl.mcr_process_order === null ||
        itemControl.mcr_condition1 === null ||
        itemControl.mcr_condition1.toString() === '0' ||
        (itemControl.mcr_condition1.toString() !== '1' && itemControl.mcr_condition2 === null) ||
        (itemControl.mcr_condition1.toString() !== '1' && itemControl.mcr_condition2 === 0) ||
        (itemControl.mcr_condition1.toString() !== '1' && itemControl.mcr_condition2?.toString() === '0') ||
        (itemControl.mcr_condition1.toString() !== '1' && itemControl.mcr_condition3 === null) ||
        (itemControl.mcr_condition1.toString() !== '1' && itemControl.mcr_condition3 === 0) ||
        (itemControl.mcr_condition1.toString() !== '1' && itemControl.mcr_condition3?.toString() === '0') ||
        itemControl.mcr_control_item === null ||
        itemControl.mcr_control_kb === null
    );
    if (indexItemError !== -1) {
      showAlertMessage('', ErrorMessage.INPUT_ERROR, InfoWord.ERROR, InfoWord.OK);
      return false;
    }
    return true;
  };

  const onRegisterData = async () => {
    const mstControlNewList = [...mstControl];
    const registData = mstControlNewList.filter((item: MstControl) => item.mcr_process_order !== null);

    const prepareDataApplyApi = registData.map(function (item) {
      return {
        mcr_condition1: item.mcr_condition1,
        mcr_condition2: item.mcr_condition2,
        mcr_condition3: item.mcr_condition3,
        mcr_control_item: item.mcr_control_item,
        mcr_control_kb: item.mcr_control_kb,
        mcr_process_order: item.mcr_process_order,
      };
    });

    if (!validateData()) return;

    try {
      await registerFunc({ variables: { input: prepareDataApplyApi } });
      await showAlertMessage('', InfoMessage.REGISTR_SUCCESS, InfoWord.SUCCESS, InfoWord.OK);
    } catch (err: any) {
      await showAlertMessage('', ErrorMessage.REGISTR_FAILED, InfoWord.ERROR, InfoWord.OK);
    }
    await searchMstControl({ variables: { input: {} } });
  };

  useEffect(() => {
    const lastIndex = mstControl.length - 1;
    const newListMstControl = [...mstControl];

    // 登録データが０件の場合と、データ編集時に新規データを追加する処理
    if (!newListMstControl[lastIndex] || !isEmptyData(newListMstControl[lastIndex])) {
      newListMstControl.push(emptyControl);
      setMstControl(newListMstControl);
    }
  }, [mstControl]);

  useEffect(() => {
    if (!mstControlData || !productMapping || !hinmeiMapping) return;

    // DBに格納されているデータを取得
    const mstControlRule = mstControlData.getMstControlRule.slice();

    const newListControl: MstControl[] = mstControlRule.map((item: MstControl) => {
      return {
        ...item,
        mpr_product_nm: item.mcr_condition1 === 2 ? productMapping[Number(item.mcr_condition2)] : undefined,
        mh_hin2_nm: item.mcr_condition1 === 4 ? hinmeiMapping[Number(item.mcr_condition2)] : undefined,
        mh_hin_nm: hinmeiMapping[Number(item.mcr_control_item)],
        isCannotEditControlCd: true,
        error: defaultErrorMstControl,
        isBeginData: true,
      };
    });
    newListControl.sort((a: MstControl, b: MstControl) => Number(a.mcr_process_order) - Number(b.mcr_process_order));
    setBeginLengthListControl(mstControlRule.length);
    setMstControl(newListControl);
  }, [mstControlData, productMapping, hinmeiMapping]);

  useEffect(() => {
    searchMstControl({ variables: { input: {} } });
    searchMstProduct({ variables: { input: {} } });
    searchMstHinmei({ variables: { input: {} } });
  }, []);

  return (
    <MainLayout>
      <>
        <div className="flex flex-col justify-center items-center">
          <p className="p-[20px]">制御ルール管理</p>
          <div className="flex w-4/5">
            <button
              onClick={onPressConfirmRegister}
              className="border-[#000000] border-[1px] p-[10px] mt-[25px] mb-[25px] text-center h-[50px] w-[100px] font-bold"
            >
              登録
            </button>
          </div>
          <table className="text-center border-collapse border-[3px] border-solid border-[#ddd] w-4/5 td:p-[10px]">
            <thead>
              <tr className="hover:opacity-80 even:bg-[#e2efd9]">{renderTableHeader}</tr>
            </thead>
            <tbody>
              {isMstControlLoading ? (
                <tr className="hover:opacity-80 even:bg-[#e2efd9]">
                  <td
                    colSpan={header.length}
                    className="text-center animate-pulse p-[8px] border-[1px] border-solid border-[#fff]"
                  >
                    Loading...
                  </td>
                </tr>
              ) : (
                renderTableData
              )}
            </tbody>
          </table>
        </div>
        {/* 条件２ */}
        {/* 条件１が品名の時 */}
        <Dialog
          open={modalhinmei.open}
          onClose={() => setModalhinmei({ open: true })}
          className="relative z-50 w-full h-full"
        >
          <div className="fixed w-full h-full inset-0 flex items-center justify-center">
            <ModalGuideHinmeiForControlRule
              setIsOpen={(value: boolean) => setModalhinmei({ ...modalhinmei, open: value })}
              onCallbackFunction={onCallbackHinmeiFunction}
            />
          </div>
        </Dialog>
        {/* 条件１が構成区分の時 */}
        <Dialog
          open={modalproduct.open}
          onClose={() => setModalproduct({ open: true })}
          className="relative z-50 w-full h-full"
        >
          <div className="fixed w-full h-full inset-0 flex items-center justify-center">
            <ModalGuideProduct
              open={modalproduct.open}
              onClose={() => setModalproduct({ open: false })}
              onClickRow={onCallbackProductFunction}
            />
          </div>
        </Dialog>
        {/* 制御対象 */}
        <Dialog
          open={modalhinmeicontrol.open}
          onClose={() => setModalhinmeicontrol({ open: true })}
          className="relative z-50 w-full h-full"
        >
          <div className="fixed w-full h-full inset-0 flex items-center justify-center">
            <ModalGuideHinmeiForControlRule
              setIsOpen={(value: boolean) => setModalhinmeicontrol({ ...modalhinmeicontrol, open: value })}
              onCallbackFunction={onCallbackHinmeicontrolFunction}
            />
          </div>
        </Dialog>
      </>
    </MainLayout>
  );
};

export default ControlRule;
